import React, { useState, useMemo } from 'react';
import { Box, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ReactMarkdown from 'react-markdown';
import { saveAs } from 'file-saver';

export default function DocumentPanel({ content, isStreaming, title, agentName, projectStatus }) {
  const [copied, setCopied] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState('');

  const displayContent = editMode ? editedContent : content;

  const wordCount = useMemo(() => {
    const text = displayContent || '';
    const words = text.trim().split(/\s+/).filter(Boolean);
    return words.length;
  }, [displayContent]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayContent || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const handleDownloadMd = () => {
    const blob = new Blob([displayContent || ''], { type: 'text/markdown;charset=utf-8' });
    saveAs(blob, 'document.md');
  };

  const handleDownloadTxt = () => {
    const blob = new Blob([displayContent || ''], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, 'document.txt');
  };

  const handleToggleEdit = () => {
    if (!editMode) {
      setEditedContent(content || '');
    }
    setEditMode(!editMode);
  };

  const statusColor = {
    'In progress': '#f59e0b',
    'Afgerond': '#10b981',
    'On Hold': '#767c7e',
    'Geannuleerd': '#ef4444',
  };

  return (
    <Box sx={{
      flex: 3,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      bgcolor: '#fff',
      borderRight: { md: '1px solid rgba(45,51,53,0.08)' },
      borderBottom: { xs: '1px solid rgba(45,51,53,0.08)', md: 'none' },
      height: { xs: '50vh', md: '100%' },
    }}>
      {/* Document Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2.5,
        py: 1.5,
        borderBottom: '1px solid #f1f4f5',
        minHeight: 48,
      }}>
        <Typography variant="h6" sx={{ fontSize: 15, fontWeight: 700, color: '#2d3335' }}>
          {title || 'Document'}
        </Typography>
        {projectStatus && (
          <Chip
            label={projectStatus}
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 600,
              bgcolor: (statusColor[projectStatus] || '#767c7e') + '1a',
              color: statusColor[projectStatus] || '#767c7e',
            }}
          />
        )}
        {agentName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
            <SmartToyIcon sx={{ fontSize: 14, color: '#526075' }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#526075' }}>{agentName}</Typography>
          </Box>
        )}
      </Box>

      {/* Document Toolbar */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 2,
        py: 0.75,
        borderBottom: '1px solid #f1f4f5',
        bgcolor: '#fafafa',
      }}>
        <Tooltip title={copied ? 'Gekopieerd!' : 'Kopieer tekst'}>
          <IconButton size="small" onClick={handleCopy} sx={{ color: copied ? '#10b981' : '#adb3b5', '&:hover': { color: '#526075' } }}>
            {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Download .md">
          <IconButton size="small" onClick={handleDownloadMd} sx={{ color: '#adb3b5', '&:hover': { color: '#526075' } }}>
            <DownloadIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download .txt">
          <IconButton size="small" onClick={handleDownloadTxt} sx={{ color: '#adb3b5', '&:hover': { color: '#526075' } }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'inherit', lineHeight: 1 }}>TXT</Typography>
          </IconButton>
        </Tooltip>
        <Tooltip title={editMode ? 'Terug naar preview' : 'Bewerk markdown'}>
          <IconButton size="small" onClick={handleToggleEdit} sx={{ color: editMode ? '#526075' : '#adb3b5', '&:hover': { color: '#526075' } }}>
            {editMode ? <VisibilityIcon sx={{ fontSize: 16 }} /> : <EditIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 11, color: '#adb3b5', mr: 1 }}>
          {wordCount} {wordCount === 1 ? 'woord' : 'woorden'}
        </Typography>
      </Box>

      {/* Document Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 4, py: 3 }}>
        <Box sx={{ maxWidth: 720, mx: 'auto' }}>
          {!displayContent && isStreaming ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#adb3b5' }}>
              <Typography sx={{ fontSize: 15, fontStyle: 'italic' }}>Genereren...</Typography>
              <Box component="span" sx={{
                display: 'inline-block',
                width: 6,
                height: 16,
                bgcolor: '#526075',
                animation: 'blink 1s infinite',
                '@keyframes blink': {
                  '0%, 50%': { opacity: 1 },
                  '51%, 100%': { opacity: 0 },
                },
              }} />
            </Box>
          ) : editMode ? (
            <Box
              component="textarea"
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              sx={{
                width: '100%',
                minHeight: 400,
                border: '1px solid rgba(45,51,53,0.08)',
                borderRadius: 2,
                p: 2,
                fontSize: 14,
                fontFamily: '"JetBrains Mono", monospace',
                lineHeight: 1.7,
                resize: 'vertical',
                outline: 'none',
                '&:focus': { borderColor: '#526075' },
              }}
            />
          ) : (
            <Box sx={{
              '& h1': { fontSize: 24, fontWeight: 700, mt: 3, mb: 1.5, color: '#2d3335', lineHeight: 1.3 },
              '& h2': { fontSize: 20, fontWeight: 700, mt: 2.5, mb: 1, color: '#2d3335', lineHeight: 1.3 },
              '& h3': { fontSize: 16, fontWeight: 600, mt: 2, mb: 0.75, color: '#2d3335', lineHeight: 1.4 },
              '& p': { fontSize: 15, lineHeight: 1.75, color: '#556071', my: 1.25 },
              '& blockquote': {
                borderLeft: '3px solid #526075',
                pl: 2,
                ml: 0,
                my: 1.5,
                color: '#4b5563',
                fontStyle: 'italic',
              },
              '& pre': {
                bgcolor: '#f1f4f5',
                borderRadius: 2,
                p: 2,
                overflow: 'auto',
                my: 1.5,
              },
              '& code': {
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 13,
              },
              '& ul, & ol': { pl: 3, my: 1 },
              '& li': { fontSize: 15, lineHeight: 1.75, color: '#556071', mb: 0.5 },
              '& a': { color: '#526075', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
              '& hr': { border: 'none', borderTop: '1px solid rgba(45,51,53,0.08)', my: 2 },
              '& table': { borderCollapse: 'collapse', width: '100%', my: 1.5 },
              '& th, & td': { border: '1px solid rgba(45,51,53,0.08)', px: 1.5, py: 1, fontSize: 14, textAlign: 'left' },
              '& th': { bgcolor: '#f8f9fa', fontWeight: 600 },
            }}>
              <ReactMarkdown>{displayContent || ''}</ReactMarkdown>
              {isStreaming && (
                <Box component="span" sx={{
                  display: 'inline-block',
                  width: 6,
                  height: 16,
                  bgcolor: '#526075',
                  ml: 0.5,
                  verticalAlign: 'text-bottom',
                  animation: 'blink 1s infinite',
                  '@keyframes blink': {
                    '0%, 50%': { opacity: 1 },
                    '51%, 100%': { opacity: 0 },
                  },
                }} />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
